import mainModule from './lib/review_tool_wasm.js';
import wasmBinaryUrl from './lib/review_tool_wasm.wasm';
import { commonConstants } from '../niri-manager/lumina/const_params';
import { modelTypes } from '../constants/model.constants.js';
import logger from '../logger';
import { logToTimber } from '../timberLogger';

let wasmModule = null;
let isModuleLoading = false;
let wasmModuleLoadingPromise = null;
const instances = {
  [modelTypes.ORIGIN]: null,
  [modelTypes.COMPARE]: null,
};

const rotationCache = {
  [modelTypes.ORIGIN]: 0,
  [modelTypes.COMPARE]: 0,
};

const maxImageRotationThreshold = commonConstants.max_image_rotation_threshold;

// Toggle verbose debug here
const DEBUG_WASM = false;

async function loadWasmModule() {
  const moduleConfig = {
    noInitialRun: true,
    locateFile(url) {
      if (url.endsWith('.wasm')) return wasmBinaryUrl;
      return url;
    },
    print: (text) => {
      if (DEBUG_WASM) console.log('[wasm]', text);
    },
    printErr: (text) => {
      if (DEBUG_WASM) console.error('[wasm]', text);
    },
  };

  const t0 = performance.now();
  try {
    const mod = await mainModule(moduleConfig);
    const t1 = performance.now();
    if (DEBUG_WASM) console.log('[wasm][perf] wasm load ms:', Math.round(t1 - t0));
    return mod;
  } catch (err) {
    throw new Error('Failed to load WASM module: ' + (err && err.message ? err.message : err));
  }
}

export function getWasmHandler(modelType) {
  return {
    selectImage({ jawName, intersectPoint, viewToSurface, eventOrigin, jawsPhotosMetadata }) {
      const instance = instances[modelType];
      if (!instance) {
        throw new Error('WASM instance not initialized. Call initialize() first.');
      }
      const t0 = performance.now();
      let image;
      try {
        image = instance.selectImage(jawName, intersectPoint, viewToSurface, eventOrigin);
      } catch (err) {
        throw new Error('Error in selectImage: ' + (err && err.message ? err.message : err));
      }
      const t1 = performance.now();
      if (DEBUG_WASM) console.log('[wasm][perf] selectImage exec ms:', Math.round(t1 - t0));
      if (DEBUG_WASM)
        console.log('[wasm] selectImage result meta:', {
          modelType,
          jawName,
          niriFileIndex: image && image.niriFileIndex,
          rotation: image && image.rotation,
          rotationDegrees: image && image.rotationDegrees,
          selectedPointOnImage: image && image.selectedPointOnImage,
        });

      if (!image) {
        return null;
      }

      const imageIdx = image.niriFileIndex;
      const rotation = image.rotationDegrees || 0;
      const prevRotation = rotationCache[modelType];
      const shouldTransform = Math.abs(Math.abs(prevRotation) - Math.abs(rotation)) < maxImageRotationThreshold;
      rotationCache[modelType] = rotation;
      const meta = jawsPhotosMetadata && jawsPhotosMetadata[jawName] ? jawsPhotosMetadata[jawName][imageIdx] : null;

      return {
        ...(meta || {}),
        selected_pt_on_image: image.selectedPointOnImage,
        rotation,
        rotationByQuarters: image.rotationByQuarters,
        shouldTransform,
        image_idx: imageIdx,
      };
    },

    async initialize({ geometries, dataJson } = {}) {
      if (instances[modelType]) return { initialized: true };

      if (!wasmModule) {
        if (!isModuleLoading) {
          isModuleLoading = true;
          // Use .then/.catch to create a shared promise for all callers.
          // This ensures that if multiple initialize() calls happen in parallel,
          // they all await the same loading process and only one loadWasmModule() runs.
          // Using await/try-catch here would not allow sharing the same promise.
          wasmModuleLoadingPromise = loadWasmModule()
            .then((mod) => {
              wasmModule = mod;
              isModuleLoading = false;
              return wasmModule;
            })
            .catch((err) => {
              isModuleLoading = false;
              throw err;
            });
        }
        // Wait for the shared promise
        await wasmModuleLoadingPromise;
      } else if (isModuleLoading) {
        // Wait for the shared promise if loading is in progress
        await wasmModuleLoadingPromise;
      }

      if (!wasmModule) {
        throw new Error('WASM module not loaded.');
      }
      if (geometries === undefined || dataJson === undefined) {
        throw new Error('Missing required parameters: geometries and dataJson.');
      }
      try {
        const geometriesForWasm = {
          upper_jaw:
            geometries.upper_jaw == null
              ? null
              : {
                  position: geometries.upper_jaw.attributes.position.array,
                  normal: geometries.upper_jaw.attributes.normal.array,
                  index: geometries.upper_jaw.index.array,
                },
          lower_jaw:
            geometries.lower_jaw == null
              ? null
              : {
                  position: geometries.lower_jaw.attributes.position.array,
                  normal: geometries.lower_jaw.attributes.normal.array,
                  index: geometries.lower_jaw.index.array,
                },
        };
        const t0 = performance.now();
        const instance = new wasmModule.ReviewToolWasm(geometriesForWasm, dataJson);
        const t1 = performance.now();
        instances[modelType] = instance;
        logger
          .info(`WASM initialized for model type:${modelType} initialization time:${Math.round(t1 - t0)}ms`)
          .data({ module: 'wasm-handler' })
          .to(['host'])
          .end();
        logToTimber({
          timberData: {
            action: `WASM initialized for model type:${modelType} initialization time:${Math.round(t1 - t0)}ms`,
            module: 'wasm-handler',
            type: 'object',
            actor: 'System',
          },
        });
      } catch (err) {
        throw new Error('Failed to create WASM instance: ' + (err && err.message ? err.message : err));
      }

      return { initialized: true };
    },
  };
}

export function __resetWasmHandlerTestState() {
  wasmModule = null;
  isModuleLoading = false;
  wasmModuleLoadingPromise = null;
  instances[modelTypes.ORIGIN] = null;
  instances[modelTypes.COMPARE] = null;
  rotationCache[modelTypes.ORIGIN] = 0;
  rotationCache[modelTypes.COMPARE] = 0;
}
